import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllUsers } from '../lib/firestore';
import { Panel, StatCard, LoadingSpinner } from '../components/ui/Cards';
import type { UserProfile } from '../shared';

export function AdminPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile && !profile.isAdmin) { navigate('/dashboard'); return; }
    getAllUsers(200).then((u: any[]) => { setUsers(u); setLoading(false); });
  }, [profile, navigate]);

  if (loading) return <LoadingSpinner />;

  const active    = users.filter(u => u.subscriptionStatus === 'active').length;
  const trial     = users.filter(u => u.subscriptionStatus === 'trial').length;
  const cancelled = users.filter(u => u.subscriptionStatus === 'cancelled').length;
  const mrr       = active * 5;

  return (
    <div className="page u-fade-up">
      <div className="page__header">
        <div>
          <div className="page__eyebrow">Admin Panel</div>
          <h1 className="page__title">147coach Overview</h1>
        </div>
      </div>

      <div className="u-stats-grid">
        <StatCard label="Total Users"  value={users.length} sub="All time" />
        <StatCard label="Active Subs"  value={active}       sub="Paying €6,50/mo" />
        <StatCard label="Free Trials"  value={trial}        sub="14-day trial" />
        <StatCard label="Cancelled"    value={cancelled}    sub="" />
        <StatCard label="MRR"          value={`€${mrr}`}    sub="Monthly recurring" />
      </div>

      <Panel title={`All Users (${users.length})`}>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Tier</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.uid}>
                  <td>{u.displayName}</td>
                  <td className="mono">{u.email}</td>
                  <td><StatusBadge status={u.subscriptionStatus} /></td>
                  <td className="mono">{u.subscriptionTier}</td>
                  <td className="mono">
                    {u.createdAt
                      ? new Date((u.createdAt as any).seconds * 1000).toLocaleDateString('en-GB')
                      : '—'}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 36, color: 'var(--chalk-faint)', fontStyle: 'italic' }}>
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function StatusBadge({ status }: { status: UserProfile['subscriptionStatus'] }) {
  const cls: Record<string, string> = {
    active:    'badge badge--active',
    trial:     'badge badge--trial',
    cancelled: 'badge badge--cancelled',
    expired:   'badge badge--expired',
  };
  const labels: Record<string, string> = {
    active: 'Active', trial: 'Trial', cancelled: 'Cancelled', expired: 'Expired',
  };
  return <span className={cls[status] ?? 'badge badge--expired'}>{labels[status] ?? status}</span>;
}